import React, { useEffect, useMemo, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { Loader2, Search, ScrollText, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function SAAudit() {
  const { tenantId, tenants } = useSuperAdminTenant();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const tenantName = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t.name])), [tenants]);

  useEffect(() => { setOffset(0); }, [tenantId, q, status]);

  useEffect(() => {
    setLoading(true);
    const params = { limit, offset };
    if (tenantId && tenantId !== "all") params.tenant_id = tenantId;
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    saApi.get("/audit", { params })
      .then(({ data }) => { setRows(data.items || []); setTotal(data.total || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [tenantId, q, status, offset]);

  return (
    <SuperAdminShell title="Audit Logs">
      <div className="px-5 md:px-8 py-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input data-testid="sa-audit-search" placeholder="Search action or path…" value={q}
                   onChange={(e) => setQ(e.target.value)}
                   className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="sa-audit-status" className="w-[160px] bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="ok">OK</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-white/50 ml-auto">
            {loading ? "…" : `${total} events`}
          </span>
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md"
             style={{ background: "rgba(255,255,255,0.02)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Tenant</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Resource</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-white/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40"><ScrollText className="w-6 h-6 mx-auto mb-2 opacity-40" />No events</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id || r._id || `${r.ts}-${r.path}`} data-testid="sa-audit-row"
                    className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2 text-white/70 whitespace-nowrap">
                    {r.ts ? new Date(r.ts).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-2 text-cyan-300/90 text-xs">{tenantName[r.tenant_id] || r.tenant_id || "—"}</td>
                  <td className="px-4 py-2 text-white/80">{r.user_email || "—"}</td>
                  <td className="px-4 py-2 text-white/90">{r.action || r.path}</td>
                  <td className="px-4 py-2 text-white/60 text-xs">{r.resource_type || "—"}</td>
                  <td className="px-4 py-2">
                    {String(r.status || "").startsWith("error") ? (
                      <Badge className="bg-red-500/15 text-red-300 border-0">error</Badge>
                    ) : (
                      <Badge className="bg-emerald-500/15 text-emerald-300 border-0">ok</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <Button data-testid="sa-audit-prev" variant="ghost" size="sm"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0 || loading}
                  className="text-white/70 hover:text-white hover:bg-white/5">
            <ChevronLeft className="w-4 h-4 mr-1" />Previous
          </Button>
          <span className="text-xs text-white/40">Page {Math.floor(offset / limit) + 1} of {Math.max(1, Math.ceil(total / limit))}</span>
          <Button data-testid="sa-audit-next" variant="ghost" size="sm"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total || loading}
                  className="text-white/70 hover:text-white hover:bg-white/5">
            Next<ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </SuperAdminShell>
  );
}
